import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

export type Lang = 'de' | 'en';

/** Reads the active language from the URL prefix. `/en` or `/en/*` => 'en', else 'de'. */
export function useLang(): Lang {
  const { pathname } = useLocation();
  if (pathname === '/en' || pathname.startsWith('/en/')) return 'en';
  return 'de';
}

/** Returns a tiny translate fn: t(germanValue, englishValue). Works for strings, JSX, arrays. */
export function useT() {
  const lang = useLang();
  return useCallback(
    <T,>(de: T, en: T): T => (lang === 'en' ? en : de),
    [lang],
  );
}

/** Prepends `/en` to internal paths when active language is English. */
export function localizedPath(path: string, lang: Lang): string {
  if (lang !== 'en') return path;
  if (path === '/') return '/en';
  if (path.startsWith('/en')) return path;
  if (path.startsWith('#') || path.startsWith('http')) return path;
  return `/en${path}`;
}

/** Strips the `/en` prefix from a localized path. */
export function stripLangPrefix(path: string): string {
  if (path === '/en') return '/';
  if (path.startsWith('/en/')) return path.slice(3);
  return path;
}

/** Hook returning a localizedPath helper bound to the current language. */
export function useLocalizedPath() {
  const lang = useLang();
  return useCallback((path: string) => localizedPath(path, lang), [lang]);
}

/** Switch the current URL to the other language while preserving the rest of the path. */
export function swapLangInPath(path: string, target: Lang): string {
  const base = stripLangPrefix(path);
  return localizedPath(base, target);
}